// SharedSkillCandidates — /dashboard/platform/shared-skills/candidates
// 候選審核佇列。filter by status + privacy_risk_level + domain；按
// quality_score DESC + created_at DESC 排。每張卡點擊跳 detail page。
// Spec §9.2.

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Inbox, AlertCircle, Bot, UserCog } from "lucide-react";
import PageHeader from "../../components/PageHeader.js";
import EmptyState from "../../components/EmptyState.js";
import Skeleton from "../../components/Skeleton.js";
import {
  listCandidates, friendlySharedSkillsError,
  DOMAIN_LABEL, RISK_LABEL, PRIVACY_LABEL, CANDIDATE_STATUS_LABEL,
  type CandidateListItem, type CandidateStatus, type SkillDomain,
  type SkillRiskLevel, type PrivacyRiskLevel,
} from "../../lib/sharedSkillsApi.js";
import { formatTaipeiDateTime } from "../../../lib/time.js";

type State =
  | { status: "loading" }
  | { status: "ready"; items: CandidateListItem[]; total: number }
  | { status: "error"; message: string };

const QUEUE_STATUSES: CandidateStatus[] = ["proposed", "reviewing", "request_changes"];
const ALL_STATUSES: CandidateStatus[] = [
  "proposed", "reviewing", "request_changes", "blocked",
  "needs_manual_redaction", "approved", "rejected",
];
const ALL_DOMAINS: SkillDomain[] = [
  "route_setup", "task_setup", "task_update", "notification_rule",
  "report_generation", "incident_handling", "onboarding",
  "multi_site_rollout", "roster_planning", "general",
];
const ALL_RISKS: SkillRiskLevel[] = ["low", "medium", "high", "critical"];
const ALL_PRIVACY: PrivacyRiskLevel[] = ["none", "low", "medium", "high", "unscoreable"];

const STATUS_BADGE: Record<CandidateStatus, string> = {
  proposed:                "bg-blue-500/15 text-blue-300",
  reviewing:               "bg-amber-500/15 text-amber-300",
  approved:                "bg-emerald-500/15 text-emerald-300",
  rejected:                "bg-zinc-500/15 text-zinc-300",
  request_changes:         "bg-orange-500/15 text-orange-300",
  blocked:                 "bg-red-500/15 text-red-300",
  needs_manual_redaction:  "bg-fuchsia-500/15 text-fuchsia-300",
};

const PRIVACY_TONE: Record<PrivacyRiskLevel, string> = {
  none:        "text-emerald-300",
  low:         "text-emerald-300",
  medium:      "text-amber-300",
  high:        "text-red-300",
  unscoreable: "text-red-300",
};

export default function SharedSkillCandidates(): React.ReactElement {
  const [state, setState] = useState<State>({ status: "loading" });
  const [statusFilter, setStatusFilter] = useState<"queue" | "all" | CandidateStatus>("queue");
  const [domainFilter, setDomainFilter] = useState<SkillDomain | "">("");
  const [riskFilter, setRiskFilter] = useState<SkillRiskLevel | "">("");
  const [privacyFilter, setPrivacyFilter] = useState<PrivacyRiskLevel | "">("");
  const [createdByFilter, setCreatedByFilter] = useState<"" | "ai" | "admin">("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setState({ status: "loading" });
    const statuses: CandidateStatus[] | undefined =
      statusFilter === "queue" ? QUEUE_STATUSES
      : statusFilter === "all" ? undefined
      : [statusFilter];
    try {
      const r = await listCandidates({
        status: statuses,
        domain: domainFilter || undefined,
        risk_level: riskFilter || undefined,
        privacy_risk_level: privacyFilter || undefined,
        created_by_type: createdByFilter || undefined,
        q: q.trim() || undefined,
        limit: 100,
      });
      setState({ status: "ready", items: r.items, total: r.total });
    } catch (e) {
      setState({ status: "error", message: friendlySharedSkillsError(e) });
    }
  }, [statusFilter, domainFilter, riskFilter, privacyFilter, createdByFilter, q]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="技能候選審核佇列"
        description="AI 自動萃取 / super_admin 手動提案的候選技能。請優先審核 quality_score 高且 privacy_risk 為 none/low 的項目。"
        actions={
          <Link
            to="/dashboard/platform/shared-skills"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-surface hover:bg-bg-inset px-3 py-1.5 text-xs transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            返回技能庫
          </Link>
        }
      />

      <div className="flex flex-wrap gap-2 rounded-xl border border-border bg-bg-surface p-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          className="rounded-md bg-bg-inset border border-border text-xs px-2 py-1.5"
        >
          <option value="queue">待審核（proposed/reviewing/request_changes）</option>
          <option value="all">全部狀態</option>
          {ALL_STATUSES.map((s) => <option key={s} value={s}>{CANDIDATE_STATUS_LABEL[s]}</option>)}
        </select>
        <select
          value={domainFilter}
          onChange={(e) => setDomainFilter(e.target.value as SkillDomain | "")}
          className="rounded-md bg-bg-inset border border-border text-xs px-2 py-1.5"
        >
          <option value="">全部 domain</option>
          {ALL_DOMAINS.map((d) => <option key={d} value={d}>{DOMAIN_LABEL[d]}</option>)}
        </select>
        <select
          value={riskFilter}
          onChange={(e) => setRiskFilter(e.target.value as SkillRiskLevel | "")}
          className="rounded-md bg-bg-inset border border-border text-xs px-2 py-1.5"
        >
          <option value="">全部風險</option>
          {ALL_RISKS.map((r) => <option key={r} value={r}>{RISK_LABEL[r]}</option>)}
        </select>
        <select
          value={privacyFilter}
          onChange={(e) => setPrivacyFilter(e.target.value as PrivacyRiskLevel | "")}
          className="rounded-md bg-bg-inset border border-border text-xs px-2 py-1.5"
        >
          <option value="">全部隱私風險</option>
          {ALL_PRIVACY.map((p) => <option key={p} value={p}>{PRIVACY_LABEL[p]}</option>)}
        </select>
        <select
          value={createdByFilter}
          onChange={(e) => setCreatedByFilter(e.target.value as "" | "ai" | "admin")}
          className="rounded-md bg-bg-inset border border-border text-xs px-2 py-1.5"
        >
          <option value="">全部來源</option>
          <option value="ai">AI 提案</option>
          <option value="admin">管理員建立</option>
        </select>
        <input
          type="search" placeholder="搜尋標題 / 摘要..." value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[180px] rounded-md bg-bg-inset border border-border text-xs px-3 py-1.5"
        />
      </div>

      {state.status === "loading" && (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20 w-full rounded-lg" />)}
        </div>
      )}
      {state.status === "error" && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300 flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />{state.message}
        </div>
      )}
      {state.status === "ready" && state.items.length === 0 && (
        <EmptyState icon={Inbox} title="目前沒有候選" description="待 AI 觸發器累積足夠跨租戶證據後，會自動產出候選；super_admin 也可手動透過 POST 建立。" />
      )}
      {state.status === "ready" && state.items.length > 0 && (
        <div className="space-y-2">
          <div className="text-2xs text-text-muted px-1">共 {state.total} 條（顯示前 {state.items.length} 條）</div>
          {state.items.map((c) => (
            <Link
              key={c.id}
              to={`/dashboard/platform/shared-skills/candidates/${c.id}`}
              className="block rounded-lg border border-border bg-bg-surface hover:bg-bg-inset transition-colors p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-text-primary truncate">{c.proposed_title}</span>
                    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-2xs ${STATUS_BADGE[c.status]}`}>
                      {CANDIDATE_STATUS_LABEL[c.status]}
                    </span>
                    <span className="inline-flex items-center gap-0.5 text-2xs text-text-muted">
                      {c.created_by_type === "ai" ? <><Bot className="h-3 w-3" /> AI</> : <><UserCog className="h-3 w-3" /> admin</>}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-text-secondary line-clamp-2">{c.proposed_summary}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-text-muted">
                    <span>domain: {DOMAIN_LABEL[c.proposed_domain]}</span>
                    <span>風險: {RISK_LABEL[c.proposed_risk_level]}</span>
                    <span className={PRIVACY_TONE[c.privacy_risk_level]}>
                      隱私: {PRIVACY_LABEL[c.privacy_risk_level]}
                    </span>
                    <span>quality: {c.quality_score.toFixed(2)}</span>
                    {c.source_thread_count > 1 && <span>來源 thread: {c.source_thread_count}</span>}
                    <span>建立於 {formatTaipeiDateTime(c.created_at)}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
